import { Injectable } from '@angular/core';
import { IItemsListDataFetchResult, IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';
import {
    IChecklist2Parameter,
    IChecklistV2,
    IChecklistV2ForList,
    IChecklistV2Viewer,
    ICheckpointAction,
    ICheckpointAnswerLogModel,
    ICheckpointQRCodeExportData,
} from '@fi-shared/checklist/checklist-v2';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { AccessRightType, ApplicationSettingsType, FiImageSize, FileType } from '@fi-shared/types';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { IChecklist2ForBadge } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';

@Injectable()
export class FiChecklist2Service {
    constructor(
        private fiBackend: FiBackendService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiLoading: LoadingService,
        private fiPrintDownloadService: FiPrintDownloadService,
    ) {}

    isChecklistShareRights(auditMode: boolean) {
        const applicationSettingsType = !auditMode
            ? ApplicationSettingsType.ChecklistV2Local
            : ApplicationSettingsType.ChecklistV2AuditLocal;
        return this.accessToApplicationTypeService.HasAccessToApplicationType(
            applicationSettingsType,
            AccessRightType.Share,
        );
    }

    isLocalAdminUserRights(auditMode: boolean): boolean {
        const localApplicationSettingsType = !auditMode
            ? ApplicationSettingsType.ChecklistV2Local
            : ApplicationSettingsType.ChecklistV2AuditLocal;
        const centralApplicationSettingsType = !auditMode
            ? ApplicationSettingsType.ChecklistV2Central
            : ApplicationSettingsType.ChecklistV2AuditCentral;
        return (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                localApplicationSettingsType,
                AccessRightType.Admin,
            ) &&
            !this.accessToApplicationTypeService.HasAccessToApplicationType(
                centralApplicationSettingsType,
                AccessRightType.Admin,
            )
        );
    }

    isAdminUserRights(auditMode: boolean): boolean {
        const applicationSettingsType = !auditMode
            ? ApplicationSettingsType.ChecklistV2Local
            : ApplicationSettingsType.ChecklistV2AuditLocal;
        return this.accessToApplicationTypeService.HasAccessToApplicationType(
            applicationSettingsType,
            AccessRightType.Admin,
        );
    }

    isReportReadRights(auditMode: boolean): boolean {
        const applicationSettingsType = !auditMode
            ? ApplicationSettingsType.ChecklistV2Reports
            : ApplicationSettingsType.ChecklistV2AuditReports;
        return this.accessToApplicationTypeService.HasAccessToApplicationType(
            applicationSettingsType,
            AccessRightType.Read,
        );
    }

    isReportWriteRights(auditMode: boolean): boolean {
        const applicationSettingsType = !auditMode
            ? ApplicationSettingsType.ChecklistV2Reports
            : ApplicationSettingsType.ChecklistV2AuditReports;
        return this.accessToApplicationTypeService.HasAccessToApplicationType(
            applicationSettingsType,
            AccessRightType.Write,
        );
    }

    isReportShareRights(auditMode: boolean) {
        const applicationSettingsType = !auditMode
            ? ApplicationSettingsType.ChecklistV2Reports
            : ApplicationSettingsType.ChecklistV2AuditReports;
        return this.accessToApplicationTypeService.HasAccessToApplicationType(
            applicationSettingsType,
            AccessRightType.Share,
        );
    }

    canEditScheduleBySharingSelection(
        item: { HasEditAccessToSchedule: boolean; HasAccessToChecklist: boolean },
        auditMode: boolean,
    ) {
        return this.isChecklistShareRights(auditMode) && item.HasEditAccessToSchedule && item.HasAccessToChecklist;
    }

    getChecklistsListData(parameter: IChecklist2Parameter) {
        const promise = this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IChecklistV2ForList>>(
            'Checklist/Checklist2/GetChecklistsListData',
            parameter,
        );
        return promise;
    }

    getChecklist(checklistId: number, auditMode: boolean) {
        const url = `Checklist/Checklist2/GetChecklist/${checklistId}?auditMode=${!!auditMode}`;
        return this.fiBackend.fetchData<IChecklistV2>(url);
    }

    getChecklistCopy(checklistId: number, auditMode: boolean) {
        const url = `Checklist/Checklist2/GetChecklistCopy/${checklistId}?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchData<IChecklistV2>(url));
    }

    saveChecklist(checklistForSave: IChecklistV2, auditMode: boolean) {
        const url = `Checklist/Checklist2/SaveChecklist?auditMode=${!!auditMode}`;
        return this.fiBackend.fetchDataPost<number>(url, checklistForSave);
    }

    deleteChecklists(checklistIds: number[], auditMode: boolean) {
        const url = `Checklist/Checklist2/DeleteChecklists?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<void>(url, checklistIds));
    }

    getLightWeightChecklists(auditMode: boolean) {
        const url = `Checklist/Checklist2/GetLightWeightChecklists?auditMode=${!!auditMode}`;
        return this.fiBackend.fetchData<INameIdObj[]>(url);
    }

    getChecklistsForInsertLink(auditMode: boolean) {
        const url = `Checklist/Checklist2/GetChecklistsForInsertLink?auditMode=${!!auditMode}`;
        return this.fiBackend.fetchData<INameIdObj[]>(url);
    }

    setChecklistIsGlobalMobileNotifications(checklistToScheduleId: number, isGlobalMobileNotifications: boolean) {
        const url = `Checklist/Checklist2/SetChecklistIsGlobalMobileNotifications/${checklistToScheduleId}?isGlobalMobileNotifications=${isGlobalMobileNotifications}`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<void>(url));
    }

    setChecklistIsMobileNotifications(checklistToScheduleId: number, isMobileNotifications: boolean) {
        const url = `Checklist/Checklist2/SetChecklistIsMobileNotifications/${checklistToScheduleId}?isMobileNotifications=${isMobileNotifications}`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<void>(url));
    }

    setChecklistIsNotApplicable(
        checklistToScheduleId: number,
        isNotApplicable: boolean,
        auditMode: boolean,
        businessUnitId?: number,
    ) {
        let url = `Checklist/Checklist2/SetChecklistIsNotApplicable/${checklistToScheduleId}?isNotApplicable=${isNotApplicable}&auditMode=${!!auditMode}`;
        if (businessUnitId) {
            url = url + `&businessUnitId=${businessUnitId}`;
        }
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<void>(url));
    }

    getChecklistAnswersCount(checklistToScheduleId: number, auditMode: boolean) {
        let url = `Checklist/Checklist2/GetChecklistAnswersCount/${checklistToScheduleId}?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchData<number>(url));
    }

    deleteChecklistAnswers(checklistToScheduleId: number, auditMode: boolean) {
        let url = `Checklist/Checklist2/DeleteChecklistAnswers/${checklistToScheduleId}?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<void>(url));
    }

    getChecklistViewer(
        checklistToScheduleId: number,
        auditMode: boolean,
        businessUnitId?: number,
        checklistPeriodId?: number,
    ) {
        let url = `Checklist/Checklist2/GetChecklistViewer/${checklistToScheduleId}?auditMode=${!!auditMode}`;
        if (businessUnitId) {
            url = url + `&businessUnitId=${businessUnitId}`;
        }
        if (checklistPeriodId) {
            url = url + `&checklistPeriodId=${checklistPeriodId}`;
        }
        return this.fiLoading.trackPromise(this.fiBackend.fetchData<IChecklistV2Viewer>(url));
    }

    getChecklistViewerByChecklist(checklistId: number, auditMode: boolean) {
        let url = `Checklist/Checklist2/GetChecklistViewerByChecklist/${checklistId}?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchData<IChecklistV2Viewer>(url));
    }

    saveChecklistViewer(checklistViewer: IChecklistV2Viewer, auditMode: boolean, businessUnitId?: number) {
        let url = `Checklist/Checklist2/SaveChecklistViewer?auditMode=${!!auditMode}`;
        if (businessUnitId) {
            url = url + `&businessUnitId=${businessUnitId}`;
        }
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<void>(url, checklistViewer));
    }

    getCheckpointAnswerLog(answerId: number) {
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchData<ICheckpointAnswerLogModel[]>(
                `Checklist/Checklist2/GetCheckpointAnswerLog/${answerId}`,
            ),
        );
    }

    /*
     * returns checkpoint action id
     */
    saveSingleCheckpointAction(checkpointId: number, checkpointAction: ICheckpointAction) {
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<number>(
                `Checklist/Checklist2/SaveSingleCheckpointAction/${checkpointId}`,
                checkpointAction,
            ),
        );
    }

    deleteCheckpointAction(checkpointActionId: number) {
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<void>(`Checklist/Checklist2/DeleteCheckpointAction/${checkpointActionId}`),
        );
    }

    exportCheckpointQRCodes(data: ICheckpointQRCodeExportData[]) {
        const url = `Checklist/Checklist2/ExportCheckpointQRCodes`;
        const fileName = 'qrcodes.pdf';

        const promise = this.fiPrintDownloadService.downloadBinaryFile(url, {
            fileName: fileName,
            size: FiImageSize.Original,
            data: data,
        });
        return promise;
    }

    printCheckpointQRCodesPdf(data: ICheckpointQRCodeExportData[]) {
        const formElement: HTMLFormElement = this.fiPrintDownloadService.getFormPrintElement(
            this.fiBackend.getRequestUrl('Checklist/Checklist2/PrintCheckpointQRCodes'),
        );
        formElement.append(this.fiPrintDownloadService.getInputElement(JSON.stringify(data), 'exportDataStr'));
        document.body.appendChild(formElement);
        formElement.submit();
        formElement.remove();
    }

    markChecklistsAsVisited(checklistIds: number[]) {
        return this.fiBackend.fetchDataPost<void>('Checklist/Checklist2/MarkChecklistsAsVisited', checklistIds);
    }

    getNotCompletedChecklists(auditMode: boolean) {
        const url = `Checklist/Checklist2/GetNotCompletedChecklists?auditMode=${!!auditMode}`;
        return this.fiBackend.fetchData<IChecklist2ForBadge[]>(url);
    }

    hasCheckpointAnswers(checkpointId: number) {
        const url = `Checklist/Checklist2/HasCheckpointAnswers/${checkpointId}`;
        return this.fiBackend.fetchData<boolean>(url);
    }
}
